import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Package, Truck } from 'lucide-react';
import axios from 'axios';
import { API_BASE } from '../utils/apiBase';

const API = API_BASE;

const OrderSuccess = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/order/${orderId}`);
        setOrder(response.data);
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-golden mx-auto"></div>
          <p className="mt-4 text-gray-700">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-dark-text mb-4">Order not found</h1>
          <Link to="/" className="btn-primary px-6 py-3 rounded-lg inline-block">
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream py-16">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle size={80} className="mx-auto text-green-500 mb-6" />
          
          <h1 className="text-4xl font-bold text-dark-text mb-4" data-testid="success-heading">
            Order Placed Successfully!
          </h1>
          
          <p className="text-xl text-gray-700 mb-8">
            Thank you for your order. Your order ID is <strong>{orderId.slice(0, 8)}</strong>
          </p>

          <div className="bg-cream p-6 rounded-lg mb-8 text-left">
            <h2 className="text-2xl font-bold text-dark-text mb-4">Order Summary</h2>
            
            <div className="space-y-3 mb-4">
              {order.items.map((item, index) => (
                <div key={index} className="flex justify-between">
                  <span>{item.product_name} x {item.quantity}</span>
                  <span>₹{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>₹{order.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Delivery:</span>
                <span>₹{order.delivery_charge.toFixed(2)}</span>
              </div>
              {order.discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount:</span>
                  <span>- ₹{order.discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-xl font-bold border-t pt-2">
                <span>Total Paid:</span>
                <span>₹{order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="text-left">
              <h3 className="font-bold text-dark-text mb-2 flex items-center gap-2">
                <Package size={20} className="text-golden" />
                Delivery Address
              </h3>
              <p className="text-gray-700 text-sm">{order.delivery_address}</p>
            </div>
            
            <div className="text-left">
              <h3 className="font-bold text-dark-text mb-2 flex items-center gap-2">
                <Truck size={20} className="text-golden" />
                Delivery Time
              </h3>
              <p className="text-gray-700 text-sm">Expected within 5-7 business days</p>
            </div>
          </div>

          <div className="flex gap-4 justify-center">
            <Link 
              to="/products" 
              className="btn-primary px-8 py-3 rounded-lg font-semibold"
              data-testid="continue-shopping"
            >
              Continue Shopping
            </Link>
            <Link 
              to="/" 
              className="btn-outline px-8 py-3 rounded-lg font-semibold"
              data-testid="go-home"
            >
              Go to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
